"use client";
import { Stack } from "@mui/material";

import SectionWrapper from "@/commonComponents/section-wrapper";
import { MotionTypography } from "@/commonComponents/motion-components";
import { Body1, H5_1 } from "@/styles/theme/components/typography.fontvariant";
import { Navigation } from "swiper/modules";
import EnhancedSwiper from "@/commonComponents/enhanced-swiper";
import SwiperNavigationButton from "@/commonComponents/swiper-navigation-button";
import InfoSlides, {
  InfoSlidesProps,
} from "@/commonComponents/cards/info-slides";
import { staggerTextChildrenLowOpacity } from "@/commonComponents/animations";
import { StaticImageData } from "next/image";
import { MLinkProps } from "@cc/m-link";
import { textEllipsis } from "@util/string-helpers";

export interface WardhaSectionProps {
  banner: string | StaticImageData;
  title: string;
  description: string;
  linkText: string;
  linkUrl: MLinkProps["href"];
  slider: {
    title: string;
    slides: InfoSlidesProps[];
  };
  navId?: string;
}

const WardhaSection = (props: WardhaSectionProps) => {
  const { banner, title, description, linkText, linkUrl, slider, navId } =
    props;
  return (
    <SectionWrapper
      navId={navId || "wardha"}
      SectionProps={{
        sx: {
          background: (theme) =>
            `${theme.palette.gradient.darkOverlay}, url(${
              typeof banner === "string" ? banner : banner.src
            })`,
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
          backgroundSize: "cover",
        },
        id: "initiatives",
      }}
      SectionHeaderProps={{
        title,
        description,
        TitleTypographyProps: {
          variant: "h3",
        },
        DescriptionTypographyProps: {
          fontSize: Body1,
          textAlign:"justify",
          maxWidth: 738,
          variants: staggerTextChildrenLowOpacity(0.8),
        },
        LinkProps: linkText
          ? {
              children: linkText,
              href: linkUrl,
            }
          : undefined,
        mb: { xs: 7, md: 9 },
      }}
    >
      <EnhancedSwiper
        slidesPerView={"auto"}
        SlideWrapperProps={{
          sx: {
            width: { xs: 301, lg: 421 },
            mr: 3,
          },
        }}
        sx={{
          overflow: { xs: "visible", xxl: "hidden" },
        }}
        navigation={{
          enabled: true,
          nextEl: ".wardha-swiper-next",
          prevEl: ".wardha-swiper-prev",
        }}
        modules={[Navigation]}
        SlideComponent={InfoSlides}
        SlideComponentProps={(index) => ({
          CardWrapperProps: {
            rowGap: 2,
            direction: "column",
          },
          ImageProps: {
            width: 421,
            height: 310,
            sx: {
              width: "100%",
              height: "100%",
              maxHeight: 310,
              maxWidth: 421,
            },
          },
          TagProps: {
            bgcolor: "#DADADA",
            color: "#575756",
          },
          DescriptionTypographyProps: {
            sx: textEllipsis(3),
          },
          initial: {
            opacity: 0,
            y: "100%",
          },
          whileInView: {
            opacity: 1,
            y: 0,
            transition: {
              duration: 0.6,
              delay: (index <= 3 ? index : 1) * 0.1,
            },
          },
          viewport: {
            once: true,
          },
        })}
        data={slider.slides}
        Slots={{
          ContainerStartChildren: (
            <Stack
              direction="row"
              alignItems={"center"}
              justifyContent={"space-between"}
              mb={{ xs: 3, md: 6 }}
            >
              {slider.title && (
                <MotionTypography variant="h3" fontSize={H5_1}>
                  {slider.title}
                </MotionTypography>
              )}
              <SwiperNavigationButton
                display={{
                  xs: "none",
                  md: "flex",
                }}
                PrevButtonProps={{
                  className: "wardha-swiper-prev",
                }}
                NextButtonProps={{
                  className: "wardha-swiper-next",
                }}
              />
            </Stack>
          ),
          ContainerEndChildren: (
            <SwiperNavigationButton
              mt={5}
              display={{
                xs: "flex",
                md: "none",
              }}
              alignSelf={"center"}
              PrevButtonProps={{
                className: "wardha-swiper-prev",
              }}
              NextButtonProps={{
                className: "wardha-swiper-next",
              }}
            />
          ),
        }}
      />
    </SectionWrapper>
  );
};

export default WardhaSection;
